import { useMemo } from "react"
import { CompanyInfo } from "./hooks/useBrandComparison"

interface TooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number
    color: string
    dataKey: string
    payload: {
      date: string
      companies: {
        [key: string]: {
          score: number
          ai_generated: boolean
          color: string
        }
      }
    }
  }>
  companies: CompanyInfo[]
  isLegendHover?: boolean
  currentCompany?: string
  competitorCompany?: string | null
}

export const ChartTooltip = ({
  active,
  payload,
  isLegendHover,
  currentCompany,
  competitorCompany,
}: TooltipProps) => {
  if (!active || !payload || !payload.length) return null

  const { date, companies: payloadCompanies } = payload[0].payload

  // Convert scores to numbers and filter companies based on hover type
  const companiesToShow = useMemo(
    () =>
      Object.entries(payloadCompanies)
        .map(([name, info]) => ({
          name,
          score: Number(info.score) || 0,
          color: info.color,
          ai_generated: info.ai_generated,
        }))
        .filter(company => {
          if (isLegendHover) {
            return (
              company.name === currentCompany ||
              company.name === competitorCompany
            )
          }
          return true
        })
        .sort((a, b) => b.score - a.score),
    [payloadCompanies, isLegendHover, currentCompany, competitorCompany],
  )

  // Calculate average score for visible companies
  const averageScore =
    companiesToShow.reduce((sum, company) => sum + company.score, 0) /
    companiesToShow.length

  // Split companies into above and below average
  const aboveAverage = companiesToShow.filter(
    company => company.score >= averageScore,
  )
  const belowAverage = companiesToShow.filter(
    company => company.score < averageScore,
  )

  // Split each section into two columns
  const aboveMidPoint = Math.ceil(aboveAverage.length / 2)
  const belowMidPoint = Math.ceil(belowAverage.length / 2)

  const aboveLeftColumn = aboveAverage.slice(0, aboveMidPoint)
  const aboveRightColumn = aboveAverage.slice(aboveMidPoint)
  const belowLeftColumn = belowAverage.slice(0, belowMidPoint)
  const belowRightColumn = belowAverage.slice(belowMidPoint)

  const CompanyRow = ({
    item,
    isAboveAverage,
    currentCompany,
  }: {
    item: (typeof companiesToShow)[0]
    isAboveAverage: boolean
    currentCompany?: string
  }) => {
    const isCurrentCompany = item.name === currentCompany

    return (
      <div
        className={`flex items-center justify-between group transition-all duration-200 px-system-xs py-system-xxs rounded-lg ${
          isCurrentCompany
            ? "bg-brand/50 border border-pink-400 shadow-lg"
            : "hover:bg-white/5"
        }`}
      >
        <div className="flex items-center gap-system-xs">
          <div
            className="w-system-xxs h-system-md rounded-full transition-all duration-200 group-hover:scale-110"
            style={{
              backgroundColor: item.color,
              boxShadow: `0 0 10px ${item.color}40`,
            }}
          />
          <div className="flex items-center justify-between gap-system-xs">
            <span
              className={`text-base group-hover:text-white transition-colors font-medium truncate capitalize ${
                isCurrentCompany ? "text-white font-semibold" : "text-white/90"
              }`}
            >
              {item.name}
            </span>
          </div>
        </div>
        <div
          className={`flex items-center gap-system-xxs ${isAboveAverage ? "text-green-400" : "text-pink-400"}`}
        >
          {item.ai_generated && (
            <span className="text-[10px] font-medium bg-pink/20 text-pink-300 px-system-xs py-system-xxs rounded">
              AI Suggested
            </span>
          )}
          <span className="text-base font-semibold min-w-system-md text-right">
            {item.score.toFixed(1)}
          </span>
          <span
            className={`text-sm ${isAboveAverage ? "text-green-400" : "text-pink-400"} opacity-90 transition-transform group-hover:translate-y-[-1px]`}
          >
            {isAboveAverage ? "↑" : "↓"}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-system-blue-900/90 backdrop-blur-sm p-system-md rounded-system-xl shadow-system-xl border-2 border-system-blue-700 min-w-[580px] z-[9999999]">
      {/* Header */}
      <div className="flex items-center justify-between mb-system-xxs pb-system-xs border-b-[2px] border-system-blue-700">
        <div>
          <h3 className="text-white/70 text-sm mb-system-xxs">BRAND SCORES</h3>
          <p className="text-white font-medium text-lg">{date}</p>
        </div>
        <div className="bg-white/10 px-system-md py-system-xxs rounded-full backdrop-blur-md border border-white/5">
          <p className="text-base text-white/90">
            Avg:{" "}
            <span className="font-semibold bg-clip-text text-white bg-gradient-to-r from-blue-400 to-purple-400">
              {averageScore.toFixed(1)}
            </span>
          </p>
        </div>
      </div>

      {/* Above Average Section */}
      <div className="pb-system-xxs border-b-[2px] border-system-blue-700">
        <div className="flex items-center gap-system-xs mb-system-xxs">
          <div className="w-system-xxs h-system-md rounded-full bg-gradient-to-b from-green-400 to-green-500 shadow-lg shadow-green-500/20" />
          <h4 className="text-sm text-white/50 uppercase tracking-wider">
            Above Average
          </h4>
        </div>
        <div className="grid grid-cols-2 gap-x-system-md gap-y-system-xxs">
          <div className="space-y-system-xxs">
            {aboveLeftColumn.map(item => (
              <CompanyRow
                key={item.name}
                item={item}
                isAboveAverage={true}
                currentCompany={currentCompany}
              />
            ))}
          </div>
          <div className="space-y-system-xxs">
            {aboveRightColumn.map(item => (
              <CompanyRow
                key={item.name}
                item={item}
                isAboveAverage={true}
                currentCompany={currentCompany}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Below Average Section */}
      <div className="pt-system-xxs">
        <div className="flex items-center gap-system-xs mb-system-xxs">
          <div className="w-system-xxs h-system-md rounded-full bg-gradient-to-b from-pink-400 to-pink-500 shadow-lg shadow-pink-500/20" />
          <h4 className="text-sm text-white/50 uppercase tracking-wider">
            Below Average
          </h4>
        </div>
        <div className="grid grid-cols-2 gap-x-system-md gap-y-system-xxs">
          <div className="space-y-system-xxs">
            {belowLeftColumn.map(item => (
              <CompanyRow
                key={item.name}
                item={item}
                isAboveAverage={false}
                currentCompany={currentCompany}
              />
            ))}
          </div>
          <div className="space-y-system-xxs">
            {belowRightColumn.map(item => (
              <CompanyRow
                key={item.name}
                item={item}
                isAboveAverage={false}
                currentCompany={currentCompany}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
